import React, { useMemo, useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MessageSquare, UserPlus, X, Check, Pencil, Bell, MessagesSquare, ChevronDown, ChevronRight } from 'lucide-react';
import { CommanServices } from '@/services/commanServices';
import { toast } from 'sonner';

const SCOPE_LABEL = { department: 'Department', user: 'Individual', crew: 'Crew' };

const ROLE_OPTIONS = [
  'Incident Commander',
  'Operations Chief',
  'Planning Chief',
  'Logistics Chief',
  'Safety Officer',
  'Liaison Officer',
  'Public Information Officer',
  'Evaluator',
  'Observer',
  'Facilitator',
  'Player',
  'Support',
];

export const ExerciseDiscussionGroup = ({
  incident,
  isAdmin,
  scenarioMode = false,
  allDepartments = [],
  appUsers = [],
  allTeams = [],
  onRefresh,
  onChat,
  onChatAll,
}) => {
  const canManage = !!isAdmin && (scenarioMode || (!!incident?.is_exercise && String(incident?.status || '').toLowerCase() === 'requested'));
  const group = incident?.discussion_group || [];

  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [tab, setTab] = useState('department');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState({}); // key `${type}:${id}` -> {type,id,name}
  const [saving, setSaving] = useState(false);
  const [notifying, setNotifying] = useState(null);
  const [recommended, setRecommended] = useState({ department: new Set(), user: new Set(), crew: new Set() });

  // Edit-row dialog state
  const [editEntry, setEditEntry] = useState(null);
  const [editForm, setEditForm] = useState({ internal_type: 'Internal', contactIds: [], role: '', notes: '' });
  const [editSaving, setEditSaving] = useState(false);

  const crews = useMemo(() => {
    const list = [];
    (allTeams || []).forEach((t) => {
      (t.crews || []).forEach((c) => {
        if (c?.id) list.push({ id: c.id, name: c.crew_name || c.name || 'Crew', team: t.team_name || '' });
      });
    });
    return list;
  }, [allTeams]);

  const existingKeys = useMemo(
    () => new Set((group || []).map((e) => `${String(e.type).toLowerCase()}:${e.id}`)),
    [group]
  );

  useEffect(() => {
    if (!open || !incident?.id) return;
    (async () => {
      try {
        const res = await CommanServices.getRecommendedEntities(incident.id);
        const r = res?.data ?? res;
        if (r && r.status !== false) {
          setRecommended({
            department: new Set((r.department_ids || []).map(String)),
            user: new Set((r.user_ids || []).map(String)),
            crew: new Set((r.crew_ids || []).map(String)),
          });
        }
      } catch (e) {
        console.error('recommended fetch failed', e);
      }
    })();
  }, [open, incident?.id]);

  const userName = (id) => {
    const u = appUsers.find((x) => String(x.id) === String(id));
    return u ? (u.full_name || u.name || u.email) : null;
  };

  const resolveName = (entry) => {
    if (entry.name) return entry.name;
    const type = String(entry.type).toLowerCase();
    if (type === 'department') return (allDepartments.find((d) => d.id === entry.id) || {}).department_name || entry.id;
    if (type === 'user') return userName(entry.id) || entry.id;
    if (type === 'crew') return (crews.find((c) => c.id === entry.id) || {}).name || entry.id;
    return entry.id;
  };

  // Candidate contact persons for a given discussion-group entry
  const getContactOptions = (entry) => {
    const type = String(entry.type).toLowerCase();
    if (type === 'user') {
      return [{ id: entry.id, name: userName(entry.id) || entry.name || entry.id, recommended: true }];
    }
    if (type === 'department') {
      const d = allDepartments.find((x) => x.id === entry.id);
      if (!d) return [];
      const leaderIds = new Set([...(d.leaders || []), d.department_leader].filter(Boolean).map(String));
      const memberIds = (d.members || []).map((m) => (typeof m === 'object' ? (m.id || m.user_id) : m)).filter(Boolean);
      const allIds = Array.from(new Set([...Array.from(leaderIds), ...memberIds.map(String)]));
      return allIds.map((id) => ({ id, name: userName(id) || id, recommended: leaderIds.has(String(id)) }));
    }
    if (type === 'crew') {
      const emails = [];
      (allTeams || []).forEach((t) =>
        (t.crews || []).forEach((c) => {
          if (c.id === entry.id) {
            (c.escalation_rules || []).forEach((r) => {
              const esc = r.escalated_to;
              (Array.isArray(esc) ? esc : [esc]).forEach((x) => { if (x) emails.push(String(x).trim()); });
            });
          }
        })
      );
      const uniq = Array.from(new Set(emails));
      return uniq.map((em) => {
        const u = appUsers.find(
          (x) => (x.email && x.email.toLowerCase() === em.toLowerCase()) || x.user_account === em || String(x.id) === em
        );
        return { id: u ? u.id : em, name: u ? (u.full_name || u.name || u.email) : em, recommended: false };
      });
    }
    return [];
  };

  const defaultContacts = (type, id, name) => {
    const opts = getContactOptions({ type, id, name });
    if (type === 'user') return opts.map((o) => ({ id: o.id, name: o.name }));
    if (type === 'department') return opts.filter((o) => o.recommended).map((o) => ({ id: o.id, name: o.name }));
    return [];
  };

  const toggle = (type, id, name) => {
    const key = `${type}:${id}`;
    setSelected((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = { type, id, name };
      return next;
    });
  };

  const sourceList = () => {
    const q = search.trim().toLowerCase();
    if (tab === 'department') {
      return allDepartments
        .map((d) => ({ id: d.id, name: d.department_name }))
        .filter((d) => d.name && (!q || d.name.toLowerCase().includes(q)));
    }
    if (tab === 'user') {
      return appUsers
        .map((u) => ({ id: u.id, name: u.full_name || u.name || u.email }))
        .filter((u) => u.name && (!q || u.name.toLowerCase().includes(q)));
    }
    return crews
      .map((c) => ({ id: c.id, name: c.team ? `${c.name} · ${c.team}` : c.name, rawName: c.name }))
      .filter((c) => c.name && (!q || c.name.toLowerCase().includes(q)));
  };

  const handleInvite = async () => {
    const entries = Object.values(selected).map((e) => ({
      type: e.type,
      id: e.id,
      name: e.name,
      internal_type: 'Internal',
      contact_persons: defaultContacts(e.type, e.id, e.name),
      role: '',
      notes: '',
      recommended: !!(recommended[e.type] && recommended[e.type].has(String(e.id))),
    }));
    if (entries.length === 0) {
      toast.error('Select at least one department, user, or crew');
      return;
    }
    setSaving(true);
    try {
      const res = await CommanServices.discussionGroupInvite(incident.id, entries);
      if (res?.status !== false) {
        toast.success('Added to the discussion group');
        setSelected({});
        setOpen(false);
        onRefresh && onRefresh();
      } else {
        toast.error(res?.message || 'Failed to add to discussion group');
      }
    } catch (e) {
      console.error('discussion invite failed', e);
      toast.error('Failed to add to discussion group');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (entry) => {
    try {
      const res = await CommanServices.discussionGroupRemove(incident.id, [{ type: entry.type, id: entry.id }]);
      if (res?.status !== false) {
        toast.success('Removed from discussion group');
        onRefresh && onRefresh();
      }
    } catch (e) {
      console.error('discussion remove failed', e);
      toast.error('Failed to remove');
    }
  };

  const openEdit = (entry) => {
    setEditEntry(entry);
    setEditForm({
      internal_type: entry.internal_type || 'Internal',
      contactIds: (entry.contact_persons || []).map((c) => (typeof c === 'object' ? c.id : c)),
      role: entry.role || '',
      notes: entry.notes || '',
    });
  };

  const toggleEditContact = (id) => {
    setEditForm((prev) => {
      const has = prev.contactIds.includes(id);
      return { ...prev, contactIds: has ? prev.contactIds.filter((x) => x !== id) : [...prev.contactIds, id] };
    });
  };

  const handleSaveEdit = async () => {
    if (!editEntry) return;
    const opts = getContactOptions(editEntry);
    const contact_persons = editForm.contactIds.map((id) => ({
      id,
      name: (opts.find((o) => String(o.id) === String(id)) || {}).name || userName(id) || id,
    }));
    setEditSaving(true);
    try {
      const res = await CommanServices.discussionGroupUpdate(incident.id, {
        type: editEntry.type,
        id: editEntry.id,
        internal_type: editForm.internal_type,
        contact_persons,
        role: editForm.role,
        notes: editForm.notes,
      });
      if (res?.status !== false) {
        toast.success('Discussion group updated');
        setEditEntry(null);
        onRefresh && onRefresh();
      } else {
        toast.error(res?.message || 'Failed to update');
      }
    } catch (e) {
      console.error('discussion update failed', e);
      toast.error('Failed to update');
    } finally {
      setEditSaving(false);
    }
  };

  const handleNotify = async (entry = null) => {
    const key = entry ? `${entry.type}:${entry.id}` : 'all';
    setNotifying(key);
    try {
      const res = await CommanServices.discussionGroupNotify(
        incident.id,
        entry ? { type: entry.type, id: entry.id } : null
      );
      if (res?.status !== false) {
        const r = res?.data ?? res;
        const who = entry ? resolveName(entry) : 'all members';
        toast.success(`Notified ${who} (${r?.notified ?? 0})`);
        onRefresh && onRefresh();
      } else {
        toast.error(res?.message || 'Failed to notify');
      }
    } catch (e) {
      console.error('discussion notify failed', e);
      toast.error('Failed to notify');
    } finally {
      setNotifying(null);
    }
  };

  if (!incident?.is_exercise) return null;

  return (
    <div data-testid="exercise-discussion-group">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <button
          type="button"
          data-testid="discussion-collapse-toggle"
          onClick={() => setCollapsed((c) => !c)}
          className="text-xs font-semibold text-gray-700 flex items-center gap-1.5 hover:text-indigo-600 transition-colors"
        >
          {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          <MessageSquare className="h-3.5 w-3.5 text-indigo-500" /> {scenarioMode ? 'Scenario Discussion Group' : (String(incident?.status || '').toLowerCase() === 'active' ? 'Active Drill Discussion Group' : 'Plan Discussion Group')}
          <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-bold">{group.length}</span>
        </button>
        {canManage && (
          <div className="flex items-center gap-2">
            {onChatAll && (
              <Button
                size="sm"
                data-testid="chat-all-discussion-btn"
                variant="outline"
                className="h-7 px-2.5 text-[11px] border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                onClick={() => onChatAll(group)}
                disabled={group.length === 0}
                title="Open the exercise discussion chat"
              >
                <MessagesSquare className="h-3.5 w-3.5 mr-1" /> Chat All
              </Button>
            )}
            <Button
              size="sm"
              data-testid="notify-discussion-btn"
              variant="outline"
              className="h-7 px-2.5 text-[11px] border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={() => handleNotify()}
              disabled={!!notifying || group.length === 0}
              title="Notify all members (in-app only)"
            >
              <Bell className="h-3.5 w-3.5 mr-1" /> {notifying === 'all' ? 'Notifying…' : 'Notify All'}
            </Button>
            <Button
              size="sm"
              data-testid="invite-discussion-btn"
              className="h-7 px-2.5 text-[11px] bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={() => setOpen(true)}
            >
              <UserPlus className="h-3.5 w-3.5 mr-1" /> Invite
            </Button>
          </div>
        )}
      </div>

      {scenarioMode && !collapsed && (
        <div className="mb-2 flex items-start gap-2 rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2 text-[11px] text-indigo-800">
          <MessageSquare className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span>Invite departments, users or crews to <b>discuss and review this scenario</b> while you design it.</span>
        </div>
      )}

      {group.length === 0 ? (
        scenarioMode && canManage ? null : (
          <div className="text-xs text-gray-400 italic py-2">
            {canManage
              ? 'No one invited yet. Invite departments, users or crews to discuss the scenario.'
              : 'No discussion group members.'}
          </div>
        )
      ) : collapsed ? null : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="w-full text-xs" data-testid="discussion-group-table">
            <thead>
              <tr className="text-left text-gray-500 bg-gray-50 border-b border-gray-200">
                <th className="py-2 px-3 font-semibold">Name</th>
                <th className="py-2 px-3 font-semibold">Scope</th>
                <th className="py-2 px-3 font-semibold">Type</th>
                <th className="py-2 px-3 font-semibold">Contact Persons</th>
                <th className="py-2 px-3 font-semibold">Role</th>
                <th className="py-2 px-3 font-semibold">Notes</th>
                {canManage && <th className="py-2 px-3 font-semibold text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {group.map((entry, i) => {
                const scope = SCOPE_LABEL[String(entry.type).toLowerCase()] || 'Department';
                const contacts = entry.contact_persons || [];
                return (
                  <tr
                    key={`${entry.type}-${entry.id}-${i}`}
                    data-testid={`discussion-entry-${entry.id}`}
                    className="border-b border-gray-100 align-top hover:bg-gray-50/60"
                  >
                    <td className="py-2 px-3 text-gray-900 font-medium">
                      <div className="flex items-center gap-1.5">
                        {resolveName(entry)}
                        {entry.recommended && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-200">
                            Recommended
                          </span>
                        )}
                      </div>
                      {entry.last_notified_at && (
                        <div className="text-[10px] text-gray-400 mt-0.5" data-testid={`discussion-last-notified-${entry.id}`}>
                          Last notified: {new Date(entry.last_notified_at).toLocaleString()}
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-3 text-gray-600">{scope}</td>
                    <td className="py-2 px-3">
                      <Badge
                        className={
                          (entry.internal_type || 'Internal') === 'External'
                            ? 'bg-purple-50 text-purple-700 border border-purple-200'
                            : 'bg-sky-50 text-sky-700 border border-sky-200'
                        }
                      >
                        {entry.internal_type || 'Internal'}
                      </Badge>
                    </td>
                    <td className="py-2 px-3 text-gray-700">
                      {contacts.length === 0 ? (
                        <span className="text-gray-400 italic">—</span>
                      ) : (
                        <div className="flex flex-col gap-1">
                          {contacts.map((c, ci) => {
                            const cid = typeof c === 'object' ? c.id : c;
                            const cname = (typeof c === 'object' ? c.name : null) || userName(cid) || cid;
                            return (
                              <div key={ci} className="flex items-center gap-1.5">
                                <span>{cname}</span>
                                {onChat && (
                                  <button
                                    type="button"
                                    data-testid={`discussion-chat-${cid}`}
                                    title={`Chat with ${cname}`}
                                    className="p-0.5 text-indigo-500 hover:text-indigo-700"
                                    onClick={() => onChat({ id: cid, name: cname })}
                                  >
                                    <MessagesSquare className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      {entry.role ? (
                        <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">{entry.role}</Badge>
                      ) : (
                        <span className="text-gray-400 italic">—</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-gray-500 max-w-[160px] truncate" title={entry.notes || ''}>
                      {entry.notes || '—'}
                    </td>
                    {canManage && (
                      <td className="py-2 px-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            data-testid={`discussion-notify-${entry.id}`}
                            className="p-1 text-gray-400 hover:text-amber-600 rounded disabled:opacity-40"
                            title="Notify this member (in-app only)"
                            disabled={!!notifying}
                            onClick={() => handleNotify(entry)}
                          >
                            <Bell className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            data-testid={`discussion-edit-${entry.id}`}
                            className="p-1 text-gray-400 hover:text-indigo-600 rounded"
                            title="Edit"
                            onClick={() => openEdit(entry)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            data-testid={`discussion-remove-${entry.id}`}
                            className="p-1 text-gray-400 hover:text-red-500 rounded"
                            title="Remove"
                            onClick={() => handleRemove(entry)}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Invite Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="invite-discussion-modal">
          <DialogHeader>
            <DialogTitle>Invite to discuss the exercise</DialogTitle>
          </DialogHeader>
          <Tabs value={tab} onValueChange={(v) => { setTab(v); setSearch(''); }}>
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="department" data-testid="invite-tab-department">Departments</TabsTrigger>
              <TabsTrigger value="user" data-testid="invite-tab-user">Users</TabsTrigger>
              <TabsTrigger value="crew" data-testid="invite-tab-crew">Crews</TabsTrigger>
            </TabsList>
            {['department', 'user', 'crew'].map((t) => (
              <TabsContent key={t} value={t} className="mt-3">
                <Input
                  placeholder={`Search ${t}s...`}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="mb-2 h-9 text-sm"
                  data-testid="invite-search-input"
                />
                <div className="max-h-64 overflow-y-auto space-y-1 pr-1">
                  {sourceList().length === 0 ? (
                    <div className="text-xs text-gray-400 italic py-4 text-center">No results</div>
                  ) : (
                    sourceList().map((opt) => {
                      const key = `${t}:${opt.id}`;
                      const already = existingKeys.has(key);
                      const checked = !!selected[key] || already;
                      return (
                        <button
                          type="button"
                          key={opt.id}
                          disabled={already}
                          data-testid={`invite-option-${opt.id}`}
                          onClick={() => toggle(t, opt.id, opt.rawName || opt.name)}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                            already
                              ? 'opacity-50 cursor-not-allowed bg-gray-50'
                              : checked
                                ? 'bg-indigo-50 border border-indigo-200'
                                : 'hover:bg-gray-50 border border-transparent'
                          }`}
                        >
                          <span className={`flex items-center justify-center w-4 h-4 rounded border ${checked ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-300'}`}>
                            {checked && <Check className="h-3 w-3" />}
                          </span>
                          <span className="flex-1 truncate">{opt.name}</span>
                          {(recommended[t] && recommended[t].has(String(opt.id))) && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-amber-100 text-amber-700 border border-amber-200 uppercase tracking-wide" data-testid={`invite-recommended-${opt.id}`}>
                              Recommended
                            </span>
                          )}
                          {already && <span className="text-[9px] font-bold text-emerald-600 uppercase">added</span>}
                        </button>
                      );
                    })
                  )}
                </div>
              </TabsContent>
            ))}
          </Tabs>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="invite-cancel-btn">Cancel</Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={handleInvite}
              disabled={saving || Object.keys(selected).length === 0}
              data-testid="invite-confirm-btn"
            >
              {saving ? 'Adding…' : `Add ${Object.keys(selected).length || ''}`.trim()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Row Modal */}
      <Dialog open={!!editEntry} onOpenChange={(v) => { if (!v) setEditEntry(null); }}>
        <DialogContent className="max-w-md" data-testid="edit-discussion-modal">
          <DialogHeader>
            <DialogTitle>Edit · {editEntry ? resolveName(editEntry) : ''}</DialogTitle>
          </DialogHeader>
          {editEntry && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-600">Type</label>
                <Select value={editForm.internal_type} onValueChange={(v) => setEditForm((p) => ({ ...p, internal_type: v }))}>
                  <SelectTrigger className="h-9 text-sm" data-testid="edit-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Internal">Internal</SelectItem>
                    <SelectItem value="External">External</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-600">
                  Contact Persons
                  {String(editEntry.type).toLowerCase() === 'user' && <span className="text-gray-400 font-normal"> (fixed for individuals)</span>}
                </label>
                <div className="max-h-40 overflow-y-auto space-y-1 border border-gray-200 rounded-lg p-1.5">
                  {getContactOptions(editEntry).length === 0 ? (
                    <div className="text-xs text-gray-400 italic py-2 text-center">No candidate contacts</div>
                  ) : (
                    getContactOptions(editEntry).map((o) => {
                      const fixed = String(editEntry.type).toLowerCase() === 'user';
                      const checked = editForm.contactIds.includes(o.id) || fixed;
                      return (
                        <button
                          key={o.id}
                          type="button"
                          disabled={fixed}
                          data-testid={`edit-contact-${o.id}`}
                          onClick={() => toggleEditContact(o.id)}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm ${checked ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-gray-50 border border-transparent'} ${fixed ? 'cursor-default' : ''}`}
                        >
                          <span className={`flex items-center justify-center w-4 h-4 rounded border ${checked ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-300'}`}>
                            {checked && <Check className="h-3 w-3" />}
                          </span>
                          <span className="flex-1 truncate">{o.name}</span>
                          {o.recommended && (
                            <span className="text-[9px] font-bold text-amber-600 uppercase">Recommended</span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-600">Role</label>
                <Select value={editForm.role || 'none'} onValueChange={(v) => setEditForm((p) => ({ ...p, role: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="h-9 text-sm" data-testid="edit-role-select"><SelectValue placeholder="Select a role" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {ROLE_OPTIONS.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-600">Notes</label>
                <textarea
                  data-testid="edit-notes-input"
                  value={editForm.notes}
                  onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))}
                  placeholder="Optional notes for this participant…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[70px] resize-y"
                />
              </div>
            </div>
          )}
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setEditEntry(null)} data-testid="edit-cancel-btn">Cancel</Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={handleSaveEdit}
              disabled={editSaving}
              data-testid="edit-save-btn"
            >
              {editSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExerciseDiscussionGroup;
